/**
 * Чернетка незбереженої форми документа (рішення власника 2026-08-24).
 *
 * Один рядок у редакторі:
 *     useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
 *
 * Що робить:
 *  • пам'ятає правки у localStorage (10 год, БЕЗ запису у базу);
 *  • при поверненні на документ пропонує відновити («знайдено незбережені
 *    зміни від 14:35») — переживає і перезавантаження, і падіння браузера;
 *  • попереджає при закритті вкладки браузера (beforeunload);
 *  • сам стирає чернетку після УСПІШНОЇ мутації на цій сторінці
 *    (збереження/проведення/видалення) — редактори правити не треба.
 *
 * Картки довідників (ч.1059, рішення власника): у них є ВКЛАДЕНІ мутації
 * (банк-рахунки, контакти, склади, ціни…), які НЕ зберігають саму картку.
 * Там `clearOnAnyMutation: false` і явний `markSaved()` у onSuccess
 * збереження/видалення — інакше додавання контакту мовчки стерло б
 * незбережені правки реквізитів.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useConfirm } from '@/components/ConfirmProvider';
import {
  USER_EDIT_EVENT, clearDraft, loadDraft, markClean, markDirty, saveDraft,
} from '@/store/formDrafts';

interface Options {
  /** Форма заповнена реальними даними (документ завантажено) — до цього
   *  моменту порівнювати нема з чим. */
  ready?: boolean;
  /** Вимкнути (напр. документ проведено — редагування заблоковане). */
  enabled?: boolean;
  /** Затримка запису, мс. */
  debounceMs?: number;
  /** Чия чернетка — у тексті пропозиції відновити («цього документа»,
   *  «цієї картки», «майстра створення організації»). */
  subject?: string;
  /** Стирати чернетку після БУДЬ-ЯКОЇ успішної мутації на сторінці (типово —
   *  так, редактори документів не правимо). Для карток довідників — false +
   *  явний markSaved(). */
  clearOnAnyMutation?: boolean;
}

export interface FormDraftControls {
  /** Форму збережено (або запис видалено): новий базовий стан, чернетку
   *  стерти. Викликати в onSuccess ДО переходу на іншу сторінку. */
  markSaved: () => void;
}

export function useFormDraft<T>(
  value: T,
  restore: (v: T) => void,
  {
    ready = true, enabled = true, debounceMs = 700,
    subject = 'цього документа', clearOnAnyMutation = true,
  }: Options = {},
): FormDraftControls {
  const { pathname } = useLocation();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const baseline = useRef<string | null>(null);
  const asked = useRef(false);
  const valueRef = useRef(value);
  valueRef.current = value;
  // ГОЛОВНЕ (фікс 2026-08-24): чернетка пишеться ЛИШЕ після реальної дії
  // користувача. Без цього форма, яку програмно заповнює useEffect даними
  // документа, виглядала «зміненою» одразу після відкриття — і система
  // питала про незбережені зміни там, де юзер нічого не чіпав (та ще й на
  // ПРОВЕДЕНОМУ документі, який редагувати не можна).
  const touched = useRef(false);
  // Відкладений запис чернетки (debounce). Тримаємо id, щоб markSaved /
  // успішна мутація його скасовували: інакше таймер, заведений до
  // натискання «зберегти», дописав би чернетку вже ПІСЛЯ збереження.
  const pendingWrite = useRef<number | null>(null);

  useEffect(() => {
    touched.current = false;
    baseline.current = null;
    asked.current = false;
    const onUserEdit = (e: Event) => {
      const el = e.target as HTMLElement | null;
      // disabled/readonly поля (проведений документ) подій не дають, але
      // перестрахуємось: правка — це input/change/paste у полі вводу.
      if (!el || !(el instanceof HTMLElement)) return;
      touched.current = true;
    };
    const host: EventTarget = document.querySelector('.app__main') ?? document;
    host.addEventListener('input', onUserEdit, true);
    host.addEventListener('change', onUserEdit, true);
    host.addEventListener('paste', onUserEdit, true);
    // ч.1059: вибір мишею у підборах / «додати рядок» (signalUserEdit).
    host.addEventListener(USER_EDIT_EVENT, onUserEdit, true);
    return () => {
      host.removeEventListener('input', onUserEdit, true);
      host.removeEventListener('change', onUserEdit, true);
      host.removeEventListener('paste', onUserEdit, true);
      host.removeEventListener(USER_EDIT_EVENT, onUserEdit, true);
    };
  }, [pathname]);

  // 1. Пропозиція відновити чернетку — коли форма «влаглася» (документ
  //    завантажено і програмне заповнення відпрацювало).
  useEffect(() => {
    if (!ready || !enabled || asked.current) return;
    const id = window.setTimeout(() => {
      asked.current = true;
      const draft = loadDraft<T>(pathname);
      if (!draft) return;
      const current = JSON.stringify(valueRef.current);
      if (JSON.stringify(draft.value) === current) {
        clearDraft(pathname);
        return;
      }
      const at = new Date(draft.ts).toLocaleString('uk-UA',
        { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' });
      void confirm({
        title: 'Незбережені зміни',
        message: `Знайдено незбережені зміни ${subject} (${at}). Відновити їх?`,
        okLabel: 'відновити',
        cancelLabel: 'відхилити',
      }).then((ok) => {
        if (ok) {
          touched.current = true;   // відновлене вважаємо правками юзера
          restore(draft.value);
        } else {
          clearDraft(pathname);
          markClean(pathname);
        }
      });
    }, 600);
    return () => window.clearTimeout(id);
  }, [ready, enabled, pathname, confirm, restore, subject]);

  // 2. Чернетка: доки юзер НЕ чіпав форму — просто тримаємо базовий стан
  //    актуальним (програмне заповнення = не зміна).
  useEffect(() => {
    if (!ready || !enabled) return;
    const serialized = JSON.stringify(value);
    if (!touched.current) {
      baseline.current = serialized;
      return;
    }
    if (baseline.current === null || serialized === baseline.current) {
      clearDraft(pathname);
      markClean(pathname);
      return;
    }
    const id = window.setTimeout(() => {
      pendingWrite.current = null;
      saveDraft(pathname, value);
      markDirty(pathname);
    }, debounceMs);
    pendingWrite.current = id;
    return () => {
      window.clearTimeout(id);
      if (pendingWrite.current === id) pendingWrite.current = null;
    };
  }, [value, ready, enabled, pathname, debounceMs]);

  // 3. Успішна мутація (зберегти/провести/видалити) — форма збережена:
  //    новий базовий стан, чернетка не потрібна.
  const markSaved = useCallback(() => {
    if (pendingWrite.current !== null) {
      window.clearTimeout(pendingWrite.current);
      pendingWrite.current = null;
    }
    baseline.current = JSON.stringify(valueRef.current);
    touched.current = false;
    clearDraft(pathname);
    markClean(pathname);
  }, [pathname]);

  useEffect(() => {
    if (!enabled || !clearOnAnyMutation) return;
    const unsub = qc.getMutationCache().subscribe((event) => {
      if (event?.mutation?.state.status !== 'success') return;
      markSaved();
    });
    return unsub;
  }, [qc, enabled, clearOnAnyMutation, markSaved]);

  // 4. Розмонтування — знімаємо позначку «брудна» (чернетка лишається).
  useEffect(() => () => markClean(pathname), [pathname]);

  return { markSaved };
}

export default useFormDraft;
